import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, X, ShieldCheck, ShieldOff, Users2, ExternalLink } from "lucide-react";
import { formatDay } from "@/lib/format";
import { formatRelativeTime } from "@/lib/relativeTime";
import { useAuth } from "@/lib/auth.jsx";
import { userTitle, roleDefaultTitle } from "@/lib/userDisplay";

const ROLE_OPTIONS = [
  { value: "vc_admin", label: "VC Admin" },
  // v5.5 Addition 19: IT Admin removed from frontend surfaces
  { value: "senior_vc", label: roleDefaultTitle("senior_vc") },
  { value: "junior_vc", label: roleDefaultTitle("junior_vc") },
];

const TABS = [
  { key: "internal", label: "Internal Users" },
  { key: "external", label: "External Users" },
  { key: "resets", label: "Password Reset Requests" },  // v5.8 A10
];

export default function UserManagement() {
  const [tab, setTab] = useState("internal");
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const { user: current } = useAuth();

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    if (u.user_id === current.user_id && role !== current.role) {
      toast.error("You cannot change your own role");
      return;
    }
    try {
      await api.patch(`/users/${u.user_id}`, { role });
      toast.success(`${u.full_name} is now ${roleDefaultTitle(role)}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change role");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.user_id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? `${u.full_name} deactivated` : `${u.full_name} reactivated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="cw-caption uppercase tracking-widest">Team</div>
          <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
            <Users2 size={24} /> User Management
          </h1>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Invite team members, assign roles, and manage access. Internal users have full workspace access; external users are scoped to specific deals.
          </p>
        </div>
        {tab === "internal" && (
          <button className="cw-btn-primary flex items-center gap-2"
            onClick={() => setShowInvite(true)} data-testid="invite-user-btn">
            <UserPlus size={16} /> Invite User
          </button>
        )}
      </div>

      <div className="mt-6 border-b flex gap-1" style={{ borderColor: "var(--cw-border)" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.key ? "" : "text-[var(--cw-muted)] border-transparent"
            }`}
            style={tab === t.key ? { color: "var(--cw-primary)", borderColor: "var(--cw-secondary)" } : {}}
            data-testid={`users-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "internal" ? (
        <div className="mt-6 cw-card overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead style={{ background: "rgba(40,42,116,0.05)" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Joined</th>
                <th className="px-4 py-3 font-semibold">Last login</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs"
                        style={{ background: "rgba(40,42,116,0.1)", color: "var(--cw-primary)" }}>
                        {(u.full_name?.[0] || "?").toUpperCase()}
                      </div>
                      <span className="font-medium">
                        {u.full_name}
                        {u.user_id === current.user_id && (
                          <span className="ml-1 text-xs text-[var(--cw-muted)]">(you)</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--cw-body)]">
                    <JobTitleCell user={u} onSaved={load} />
                  </td>
                  <td className="px-4 py-3 text-[var(--cw-muted)]">{u.email}</td>
                  <td className="px-4 py-3">
                    <select value={u.role}
                      disabled={u.user_id === current.user_id}
                      onChange={(e) => changeRole(u, e.target.value)}
                      className="h-8 px-2 rounded-md border text-xs bg-white disabled:opacity-70"
                      style={{ borderColor: "var(--cw-border)" }}
                      data-testid={`role-select-${u.email}`}>
                      {ROLE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className="cw-badge" style={{
                      background: u.is_active ? "rgba(44,154,82,0.15)" : "rgba(0,0,0,0.06)",
                      color: u.is_active ? "#1F6E3A" : "#666",
                    }}>
                      {u.is_active ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--cw-muted)]">{formatDay(u.created_at)}</td>
                  <td className="px-4 py-3 text-[var(--cw-muted)]"
                      title={formatRelativeTime(u.last_login_at).exact || "No login recorded"}
                      data-testid={`user-last-login-${u.email}`}>
                    {formatRelativeTime(u.last_login_at).relative}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditUser(u)}
                      className="cw-btn-ghost text-xs inline-flex items-center gap-1 mr-2"
                      data-testid={`edit-user-${u.email}`}
                    >
                      Edit
                    </button>
                    <button onClick={() => toggleActive(u)}
                      disabled={u.user_id === current.user_id}
                      className="cw-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                      data-testid={`toggle-active-${u.email}`}>
                      {u.is_active
                        ? <><ShieldOff size={12} /> Deactivate</>
                        : <><ShieldCheck size={12} /> Reactivate</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="p-10 text-center text-[var(--cw-muted)]">No users yet.</div>
          )}
        </div>
      ) : tab === "external" ? (
        <div className="mt-6 cw-card p-10 flex flex-col items-center text-center" data-testid="external-users-empty">
          <div className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}>
            <ExternalLink size={22} />
          </div>
          <h2 className="cw-h2 mt-4">External Users — Coming Soon</h2>
          <p className="text-sm text-[var(--cw-muted)] mt-2 max-w-md">
            Grant deal-level access to external collaborators (advisors, LPs, technical reviewers) without giving them workspace access. Contact{" "}
            <a href="mailto:hello@theprodzen.com" className="underline" style={{ color: "var(--cw-primary)" }}>
              hello@theprodzen.com
            </a>{" "}for early access.
          </p>
        </div>
      ) : (
        <PasswordResetRequests />
      )}

      {showInvite && (
        <InviteModal onClose={() => setShowInvite(false)} onInvited={() => { setShowInvite(false); load(); }} />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          isSelf={editUser.user_id === current.user_id}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(); }}
        />
      )}
    </div>
  );
}

/* --------------- v5.6 #4: Full Edit User Modal --------------- */
function EditUserModal({ user, isSelf, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: user.full_name || "",
    email: user.email || "",
    job_title: user.job_title || "",
    role: user.role,
    is_active: !!user.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(false);

  const border = { borderColor: "var(--cw-border)" };
  const inp = "w-full h-9 px-3 rounded-md border text-sm mt-1";

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(form).forEach((k) => {
        if (form[k] !== user[k]) payload[k] = form[k];
      });
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      await api.patch(`/users/${user.user_id}/full`, payload);
      toast.success("User updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const attemptSave = () => {
    if (form.email.toLowerCase() !== user.email.toLowerCase()) {
      setConfirmEmail(true);
      return;
    }
    doSave();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="edit-user-modal">
      <div className="bg-white rounded-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b flex justify-between" style={border}>
          <h2 className="cw-h2">Edit User</h2>
          <button onClick={onClose} className="text-[var(--cw-muted)] text-lg">×</button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <div className="cw-caption">Full name</div>
            <input value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className={inp} style={border}
              data-testid="edit-user-fullname" />
          </div>
          <div>
            <div className="cw-caption">Email (login)</div>
            <input value={form.email} type="email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inp} style={border}
              data-testid="edit-user-email" />
          </div>
          <div>
            <div className="cw-caption">Title</div>
            <input value={form.job_title}
              onChange={(e) => setForm({ ...form, job_title: e.target.value })}
              placeholder="e.g. Managing Partner, Principal Analyst"
              className={inp} style={border}
              data-testid="edit-user-title" />
            <div className="text-[10px] text-[var(--cw-muted)] mt-1">
              Free-text display label. Role below drives permissions.
            </div>
          </div>
          <div>
            <div className="cw-caption">Role</div>
            <select value={form.role}
              disabled={isSelf}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className={inp + " disabled:opacity-70"} style={border}
              data-testid="edit-user-role">
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {isSelf && (
              <div className="text-[10px] text-[var(--cw-muted)] mt-1">
                You cannot change your own role.
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox"
              checked={form.is_active}
              disabled={isSelf}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="edit-user-active" />
            Active
          </label>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2" style={border}>
          <button onClick={onClose} className="cw-btn-ghost text-sm">Cancel</button>
          <button
            onClick={attemptSave}
            disabled={saving}
            className="cw-btn-primary text-sm"
            data-testid="edit-user-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {confirmEmail && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          data-testid="email-confirm-modal">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-5 py-3 border-b" style={border}>
              <h3 className="cw-h3">Confirm email change</h3>
            </div>
            <div className="p-5 text-sm">
              Changing this user's email will require them to log in with the new email
              (<strong>{form.email}</strong>) going forward. Continue?
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2" style={border}>
              <button onClick={() => setConfirmEmail(false)} className="cw-btn-ghost text-xs">Cancel</button>
              <button
                onClick={() => { setConfirmEmail(false); doSave(); }}
                className="cw-btn-primary text-xs"
                data-testid="email-confirm-yes"
              >
                Yes, change email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function JobTitleCell({ user, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(user.job_title || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.user_id}`, {
        job_title: val.trim(),
      });
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input value={val} onChange={(e) => setVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(user.job_title || ""); setEditing(false); } }}
          autoFocus
          placeholder={roleDefaultTitle(user.role)}
          className="h-7 px-2 rounded border text-xs w-40"
          style={{ borderColor: "var(--cw-border)" }}
          data-testid={`job-title-input-${user.email}`}
          disabled={saving} />
      </div>
    );
  }
  return (
    <button
      onClick={() => { setVal(user.job_title || ""); setEditing(true); }}
      className="text-left hover:underline"
      data-testid={`job-title-cell-${user.email}`}
    >
      {userTitle(user)}
      {!user.job_title && (
        <span className="ml-1 cw-caption">(default)</span>
      )}
    </button>
  );
}

function InviteModal({ onClose, onInvited }) {
  const [form, setForm] = useState({
    full_name: "", email: "", role: "junior_vc", job_title: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/users", {
        ...form,
        email: form.email.toLowerCase().trim(),
      });
      toast.success(`Invited ${data.email}. A welcome email with a set-password link has been sent.`);
      onInvited();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    } finally {
      setSubmitting(false);
    }
  };

  const inp = "w-full h-10 px-3 rounded-md border text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="invite-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--cw-border)" }}>
          <h2 className="cw-h2 inline-flex items-center gap-2"><UserPlus size={20} /> Invite Team Member</h2>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <F label="Full Name *">
            <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
              className={inp} style={{ borderColor: "var(--cw-border)" }} data-testid="invite-name-input" />
          </F>
          <F label="Email *">
            <input required type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className={inp} style={{ borderColor: "var(--cw-border)" }} data-testid="invite-email-input" />
          </F>
          <F label="Role *">
            <select value={form.role} onChange={(e) => set("role", e.target.value)}
              className={inp + " bg-white"} style={{ borderColor: "var(--cw-border)" }}
              data-testid="invite-role-select">
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </F>
          <F label="Job Title">
            <input value={form.job_title} onChange={(e) => set("job_title", e.target.value)}
              placeholder={`e.g. ${roleDefaultTitle(form.role)}`}
              className={inp} style={{ borderColor: "var(--cw-border)" }}
              data-testid="invite-job-title-input" />
          </F>
          <div className="text-xs text-[var(--cw-muted)]">
            They'll receive a welcome email with a secure link to set their own password. The link is valid for 7 days.
          </div>
        </div>
        <div className="border-t px-6 py-4 flex justify-end gap-2" style={{ borderColor: "var(--cw-border)" }}>
          <button type="button" onClick={onClose} className="cw-btn-ghost">Cancel</button>
          <button type="submit" disabled={submitting} className="cw-btn-primary" data-testid="submit-invite-btn">
            {submitting ? "Inviting…" : "Send Invite"}
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="cw-caption block mb-1">{label}</label>
      {children}
    </div>
  );
}


/* v5.8 A10 — Password Reset Requests panel */
function PasswordResetRequests() {
  const [rows, setRows] = useState([]);
  const [approved, setApproved] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const load = () => api.get("/password-reset-requests", { params: { status: statusFilter } })
    .then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [statusFilter]);

  const review = async (row, action) => {
    if (action === "deny" && !window.confirm(`Deny password reset for ${row.user_email}?`)) return;
    try {
      const { data } = await api.post(`/password-reset-requests/${row.request_id}/review`, { action });
      if (data.status === "approved") setApproved(data);
      else toast.success(`Request ${data.status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const resetUrl = (t) => `${window.location.origin}/reset-password?token=${t}`;
  const copy = (t) => {
    navigator.clipboard.writeText(resetUrl(t));
    toast.success("Reset link copied to clipboard");
  };

  return (
    <div className="mt-6 space-y-4" data-testid="password-reset-panel">
      <div className="flex items-center gap-3">
        {["pending", "approved", "denied", "used", "expired"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className="px-3 py-1.5 rounded-md text-xs font-medium capitalize"
            style={statusFilter === s
              ? { background: "var(--cw-primary)", color: "white" }
              : { background: "rgba(0,0,0,0.05)", color: "var(--cw-muted)" }}
            data-testid={`reset-filter-${s}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="cw-card overflow-hidden">
        <table className="w-full text-sm" data-testid="reset-requests-table">
          <thead style={{ background: "rgba(40,42,116,0.05)" }}>
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">User</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Requested</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--cw-muted)]">No requests</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.request_id} className="border-t"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid={`reset-row-${r.user_email}`}>
                <td className="px-4 py-3">{r.user_full_name || "—"}</td>
                <td className="px-4 py-3 text-[var(--cw-muted)]">{r.user_email}</td>
                <td className="px-4 py-3 text-[var(--cw-muted)]">{r.requested_at}</td>
                <td className="px-4 py-3 capitalize">{r.status}</td>
                <td className="px-4 py-3 text-right">
                  {r.status === "pending" ? (
                    <div className="flex gap-2 justify-end">
                      <button className="cw-btn-secondary text-xs"
                        onClick={() => review(r, "deny")}
                        data-testid={`reset-deny-${r.user_email}`}>Deny</button>
                      <button className="cw-btn-primary text-xs"
                        onClick={() => review(r, "approve")}
                        data-testid={`reset-approve-${r.user_email}`}>Approve</button>
                    </div>
                  ) : r.status === "approved" && r.reset_token ? (
                    <button className="cw-btn-secondary text-xs"
                      onClick={() => copy(r.reset_token)}
                      data-testid={`reset-copy-${r.user_email}`}>Copy Link</button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {approved && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="cw-card p-6 w-full max-w-md" data-testid="approved-token-modal">
            <h3 className="cw-h3 mb-3">Reset link ready</h3>
            <p className="text-xs text-[var(--cw-muted)] mb-3">
              Send this link to <strong>{approved.user_full_name || approved.user_email}</strong> using your team's usual channel.
              It expires on {approved.token_expires_at} and can only be used once.
            </p>
            <div className="p-2 rounded-md bg-black/5 font-mono text-xs break-all mb-3"
              data-testid="approved-token-url">
              {resetUrl(approved.reset_token)}
            </div>
            <div className="flex justify-end gap-2">
              <button className="cw-btn-secondary" onClick={() => copy(approved.reset_token)}>Copy</button>
              <button className="cw-btn-primary" onClick={() => setApproved(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
